/**
 * 庫存餘額**行列表**（頁 10 用）—— 含「帳本對沖行」開關（`decisions/019`／2026-09-28 產品負責人查問）
 *
 * ⚠️ 為何要呢個組件：`Inventory.tsx` 已經接近 ≤200 行硬上限（硬規則 B）→ 行列表拆出嚟。
 *
 * ⭐ 帳本對沖（double-entry）：每筆入庫只寫**一筆**移動 `supplier → warehouse`，
 *   而「位置餘額」係逐個位置嘅**淨數** → **來源位置（例：`supplier`）一定係等量負數**。
 *   全部位置加埋 ＝ 0（`summary.invariantOk`）。負數**唔係**實際存量，亦**唔影響倉租**
 *   （計租只有 `warehouse` ＋ `damaged`）—— 所以默認**收起**，只顯示「有幾多行被收起」＋開關。
 * ⚠️ 例外：**計租位置**（`warehouse`／`damaged`）出現負數 ＝ 真問題（帳本唔自洽）→ **一定照顯示**。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { card, hint as hintStyle } from '@coeldery/ui-kit';
import { LocationChip } from './StockChips';
import type { BalanceRow } from '../lib/api';

/** 呢啲位置負數 ＝ 真問題（唔准收埋）：計租位置 ＝ `decisions/003` §4 嘅 `BILLABLE_LOCATIONS` */
const NEGATIVE_MUST_SHOW = ['warehouse', 'damaged'];

/** 去掉浮點尾巴（顯示用；計數唔准前端再算） */
const fmt = (n: number): string => String(Math.round(n * 1e6) / 1e6);

/** 「帳本對沖行」＝ 負數（該位置淨流出多過流入 → 只會係另一邊）；計租位置例外 */
export const isMirrorRow = (r: BalanceRow): boolean => r.cbm < 0 && !NEGATIVE_MUST_SHOW.includes(r.location);

export default function InventoryRows({ rows, role }: { rows: BalanceRow[]; role: string }) {
  const { t } = useTranslation();
  const [showMirror, setShowMirror] = useState(false);

  const mirror = rows.filter(isMirrorRow);
  const visible = showMirror ? rows : rows.filter((r) => !isMirrorRow(r));

  return (
    <>
      {mirror.length > 0 && (
        <p style={hintStyle}>
          {t('inv.mirrorHidden', { n: mirror.length })}{' '}
          <label style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={showMirror} onChange={(e) => setShowMirror(e.target.checked)} />
            {t('inv.showMirror')}
          </label>
          <br />
          {t('inv.mirrorHint')}
        </p>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {visible.map((r) => (
          <li key={`${r.itemId}|${r.batchId}|${r.location}`} style={{ ...card, padding: '12px 14px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{r.itemName}</strong>
              <LocationChip location={r.location} />
            </div>
            <p style={hintStyle}>
              {r.barcode}
              {r.batchNo ? ` · ${t('inv.batch')} ${r.batchNo}` : ''}
              {role === 'supplier' ? '' : ` · ${r.supplierCode}`}
            </p>
            <p style={hintStyle}>
              {t('inv.cartons')}：{r.cartons}
              {r.units > 0 ? ` · ${t('inv.units')}：${r.units}` : ''} · cbm：{fmt(r.cbm)}
            </p>
          </li>
        ))}
      </ul>
    </>
  );
}
